// Command dependency contract (EPIC #157, phase P5 — design §2.2).
//
// A `command` is a plain async function `(deps, args)` that owns a user operation
// (submit / branch / reforecast / …). This module defines the SHARED half of `deps`
// — the external store + the I/O services — and `makeDefaultDeps()`, which wires the
// real production singletons. Tests call the same command with `services` swapped for
// `makeFakeForecastService(scenario)` (Lane A), so a passing command test exercises the
// REAL client over a faked wire.
//
// Commands that also need React-bound handles (UI setters, imperative refs) extend this
// base with their own extra bags — see `SubmitDeps` in `submitForecast.ts`. Those extra
// bags are the honest cost of a verbatim extraction and shrink as P6 (boot/effects) and
// P7 (UI-state sweep) move that state into the store / down into components.
import { runStore } from "../store/runStore.js";
import { genForecastFromServer, streamBranch } from "../lib/forecast/forecastClient.js";
import { resumeForecastStream } from "../lib/forecastStream.js";
import { callLlm } from "../lib/forecast/llmClient.js";
import { forecastApi, type ForecastApi } from "../services/forecastApi.js";
import type { RunAction, RunState } from "../lib/forecast/reducer.js";

/**
 * The subset of the external run-store a command uses: read via `getState()`, write via
 * `dispatch(action)`, observe via `subscribe(listener)`. Structurally satisfied by the
 * zustand vanilla `runStore` (and by a fresh test store).
 */
export interface RunStore {
  getState: () => RunState;
  dispatch: (action: RunAction) => RunAction;
  subscribe: (listener: () => void) => () => void;
}

/**
 * The I/O surface a command performs network work through (design §2.2: "no `fetch`
 * outside `services`"). `forecastClient` is exactly the shape `makeFakeForecastService`
 * returns, so the command's production wiring and its test double are interchangeable.
 * Later command PRs widen this (streamBranch, resumeForecastStream, apiClient, llm, …).
 */
export interface CommandServices {
  forecastClient: {
    // Typed at the boundary as permissive async fns: the concrete client lives in a
    // still-JS module whose inferred param types (e.g. `signal: null`) are too narrow to
    // be useful here. Widened deliberately (decision C) — Lane B's generated wire types
    // will tighten the payloads when they land.
    genForecastFromServer: (...args: any[]) => Promise<any>;
    streamBranch: (...args: any[]) => Promise<any>;
    // Resume/reattach SSE — an async generator of frame events. Consumed by the
    // resume subsystem (P5.6) via `for await`; typed loosely at the boundary (the
    // concrete generator lives in a still-JS module).
    resumeForecastStream: (...args: any[]) => AsyncIterable<any>;
  };
  /** Locked-down LLM passthrough (POST /api/forecast/llm) — used by chat-intent classification. */
  llm: (
    prompt: string,
    opts?: { signal?: AbortSignal | null; webSearch?: boolean; turnId?: string | null },
  ) => Promise<string>;
  /**
   * The TYPED REST seam (design §2.2: "no `fetch` outside `services`"). `services/forecastApi`
   * — typed domain methods (`getResult`/`cancel`/`preprocess`/`listForecasts`/…) over
   * `lib/apiClient`, so the raw `{ok,status,body}` envelope + its edge cases (null body,
   * 404-recovery) are handled once in the client rather than in every command. A test can
   * inject a fake method returning a typed fixture (no `globalThis.fetch` stubbing needed).
   */
  api: ForecastApi;
}

/** The base command deps every command shares: external store + defaulted real services. */
export interface CommandDeps {
  store: RunStore;
  services: CommandServices;
}

/**
 * Production wiring: the real module-singleton run-store + the real forecast client.
 * Tests build `{ ...makeDefaultDeps(), services: fake.forecastClient-shaped }` (plus the
 * per-command React bags) so only the wire is faked.
 */
export function makeDefaultDeps(): CommandDeps {
  return {
    store: runStore as unknown as RunStore,
    services: {
      forecastClient: { genForecastFromServer, streamBranch, resumeForecastStream },
      llm: callLlm,
      api: forecastApi,
    },
  };
}
